import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { de } from "@ainkauf/core";
import { ListScreen } from "@/screens/ListScreen";
import { ListsScreen } from "@/screens/ListsScreen";
import { ShoppingScreen } from "@/screens/ShoppingScreen";
import { PlanScreen } from "@/screens/PlanScreen";
import { RecipeScreen } from "@/screens/RecipeScreen";
import { SpendingScreen } from "@/screens/SpendingScreen";
import { HistoryScreen } from "@/screens/HistoryScreen";
import { ProductScreen } from "@/screens/ProductScreen";
import { SettingsScreen } from "@/screens/SettingsScreen";
import { LoginScreen } from "@/screens/LoginScreen";
import { FamilyGateScreen } from "@/screens/FamilyGateScreen";
import { clearInvite, pendingInvite, useAuth } from "@/lib/auth";
import { getSupabase } from "@/lib/supabase";
import { adoptLocalData } from "@/db/repository";
import { startSync } from "@/sync/runner";
import { listIdFromPath, productIdFromPath, recipeIdFromPath, shoppingListIdFromPath, useRoute } from "@/lib/router";

interface Membership {
  id: string;
  family_id: string;
}

/**
 * Eingang der App. Drei Fälle:
 *
 * 1. Keine Supabase-Instanz konfiguriert: alles läuft lokal gegen Dexie. So
 *    bleibt das Gerüst ohne Datenbank lauffähig.
 * 2. Angemeldet, aber ohne Familie: anlegen oder beitreten.
 * 3. Angemeldet und in einer Familie: Liste, und der Sync läuft.
 */
export function App() {
  const { status, session } = useAuth();
  const path = useRoute();
  const inviteCode = new URLSearchParams(window.location.search).get("code") ?? undefined;

  const membership = useQuery({
    queryKey: ["membership", session?.user.id],
    enabled: status === "angemeldet",
    queryFn: async (): Promise<Membership | null> => {
      const client = getSupabase();
      if (!client) return null;
      const { data, error } = await client
        .from("members")
        .select("id, family_id")
        .is("deleted_at", null)
        .limit(1)
        .maybeSingle();
      if (error) throw new Error(error.message);
      return data;
    },
  });

  const familyId = membership.data?.family_id ?? null;
  const userId = session?.user.id ?? null;

  // Erst übernehmen, dann rendern.
  //
  // Sonst legt die Listenansicht ihre Standardliste an, während die Übernahme
  // noch läuft: Die Liste gehört dann der lokalen Familie, die aktive ist
  // schon die echte, und das Hinzufügen tut stillschweigend nichts. Effekte
  // laufen in React von innen nach außen, dieses Rennen ist also der
  // Normalfall und nicht der Ausnahmefall.
  const [uebernommen, setUebernommen] = useState(false);
  useEffect(() => {
    if (familyId === null || userId === null) return;

    // Notausgang: hängt die Übernahme, sieht der Nutzer sonst ewig "Lädt".
    // Lieber die App zeigen und die Übernahme im Hintergrund zu Ende laufen
    // lassen, als jemanden aussperren.
    const notausgang = window.setTimeout(() => {
      setUebernommen(true);
    }, 3000);

    void adoptLocalData(familyId, userId).finally(() => {
      window.clearTimeout(notausgang);
      setUebernommen(true);
    });

    return () => {
      window.clearTimeout(notausgang);
    };
  }, [familyId, userId]);

  useSyncRunner(familyId);

  if (status === "ohne-backend") return <Routes path={path} />;
  if (status === "lädt") return <Splash />;
  if (status === "abgemeldet") return <LoginScreen inviteCode={inviteCode} />;
  if (membership.isPending) return <Splash />;
  if (membership.data === null) {
    // Wer über einen Einladungslink kam, soll nicht noch einmal tippen müssen.
    const code = pendingInvite();
    if (code !== null) {
      void joinWithInvite(code).then(() => membership.refetch());
      return <Splash />;
    }

    return (
      <FamilyGateScreen
        onDone={() => {
          void membership.refetch();
        }}
      />
    );
  }
  if (!uebernommen) return <Splash />;
  return <Routes path={path} />;
}

/**
 * Die Wege der App. "/" ist bewusst die Standardliste und nicht die Übersicht:
 * im Alltag will man die Liste sehen, nicht eine Liste von Listen.
 */
function Routes({ path }: { path: string }) {
  if (path === "/einstellungen") return <SettingsScreen />;
  if (path === "/listen") return <ListsScreen />;
  if (path === "/planen") return <PlanScreen />;
  if (path === "/ausgaben") return <SpendingScreen />;
  if (path === "/historie") return <HistoryScreen />;

  const recipeId = recipeIdFromPath(path);
  if (recipeId !== null) return <RecipeScreen recipeId={recipeId} />;

  const productId = productIdFromPath(path);
  if (productId !== null) return <ProductScreen productId={productId} />;

  const shoppingId = shoppingListIdFromPath(path);
  if (shoppingId !== null) return <ShoppingScreen listId={shoppingId} />;

  const listId = listIdFromPath(path);
  if (listId !== null) return <ListScreen listId={listId} />;

  return <ListScreen />;
}

/**
 * Startet den Abgleich, sobald eine Familie feststeht, und räumt ihn wieder ab.
 * Der Runner ist ein externes System, kein Zustand der Oberfläche, deshalb
 * lebt er im Effekt und nicht in useState.
 */
function useSyncRunner(familyId: string | null): void {
  useEffect(() => {
    const client = getSupabase();
    if (!client || familyId === null) return;

    const runner = startSync(client, familyId);
    return () => {
      runner.stop();
    };
  }, [familyId]);
}

/** Löst einen gemerkten Einladungscode ein. Scheitert er, bleibt der Bildschirm zum Eintippen. */
async function joinWithInvite(code: string): Promise<void> {
  const client = getSupabase();
  clearInvite();
  if (!client) return;

  const { error } = await client.rpc("join_family", { invite_code: code });
  if (error) console.warn("Einladungscode konnte nicht eingelöst werden:", error.message);
}

function Splash() {
  return (
    <div className="flex min-h-dvh items-center justify-center text-ink-2">
      <span>{de.common.loading}</span>
    </div>
  );
}
